import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { Users, Search, Trash2, ShieldOff, ShieldCheck, AlertTriangle, X } from 'lucide-react';
import Pagination from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // { user, mode: 'soft' | 'hard' }
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadUsers = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const data = await adminAPI.getUsers(p, ps, roleFilter, search);
      setUsers(data.users);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, pageSize, roleFilter, search]);

  useEffect(() => { loadUsers(1, pageSize); setPage(1);}, [roleFilter, search, pageSize]);
  useEffect(() => { loadUsers(page, pageSize);}, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const toggleDisable = async (user) => {
    try {
      await adminAPI.updateUser(user.id, { is_disabled: !user.is_disabled });
      loadUsers(page);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const openDelete = (user) => {
    setDeleteTarget({ user, mode: 'soft' });
    setConfirmEmail('');
    setDeleteError('');
  };
  const closeDelete = () => { setDeleteTarget(null); setConfirmEmail(''); setDeleteError(''); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { user, mode } = deleteTarget;
    if (mode === 'hard' && confirmEmail.trim().toLowerCase() !== (user.email || '').toLowerCase()) {
      setDeleteError('Type the exact email to confirm permanent deletion.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await adminAPI.deleteUser(user.id, mode === 'hard');
      closeDelete();
      loadUsers(page);
    } catch (e) {
      setDeleteError(e.response?.data?.detail || e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const roleColors = {
    applicant: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    recruiter: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    admin: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Users</h1><p className="text-slate-500 dark:text-zinc-500 text-sm">{total} total users</p></div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search by email..." className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 w-64" data-testid="user-search-input" />
          </div>
          <button type="submit" className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="user-search-btn">Search</button>
        </form>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300" data-testid="role-filter">
          <option value="">All Roles</option>
          <option value="applicant">Applicant</option>
          <option value="recruiter">Recruiter</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {loading ? <p className="text-slate-500 dark:text-zinc-500">Loading...</p> : users.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center"><Users className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" /><p className="text-slate-500 dark:text-zinc-500">No users found</p></div>
      ) : (
        <>
          <ScrollHint>
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 dark:border-zinc-800/50">
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Verified</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Joined</th>
                  <th className="text-right px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-200 dark:border-zinc-800/30 last:border-0 hover:bg-slate-100 dark:hover:bg-zinc-800/20" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-zinc-300">{u.email}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded capitalize ${roleColors[u.role] || 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>{u.role}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs ${u.is_email_verified ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-zinc-500'}`}>{u.is_email_verified ? 'Yes' : 'No'}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${u.is_disabled ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>{u.is_disabled ? 'Disabled' : 'Active'}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleDisable(u)} className={`p-1.5 rounded ${u.is_disabled ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10' : 'text-amber-600 dark:text-amber-400 hover:bg-amber-500/10'}`} title={u.is_disabled ? 'Enable' : 'Disable'} data-testid={`toggle-disable-${u.id}`}>
                          {u.is_disabled ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => openDelete(u)} className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded" title="Delete" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </ScrollHint>
        </>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        testIdPrefix="admin-users-pg"
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="delete-user-modal" onClick={closeDelete}>
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-slate-200 dark:border-zinc-800">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${deleteTarget.mode === 'hard' ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
                  <AlertTriangle className={`w-5 h-5 ${deleteTarget.mode === 'hard' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                    {deleteTarget.mode === 'hard' ? 'Permanently delete user' : 'Archive user'}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-zinc-500 mt-0.5">{deleteTarget.user.email}</p>
                </div>
              </div>
              <button onClick={closeDelete} className="p-1 text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300" data-testid="delete-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex gap-2 p-1 bg-slate-100 dark:bg-zinc-900 rounded-lg">
                <button
                  onClick={() => { setDeleteTarget({ ...deleteTarget, mode: 'soft' }); setDeleteError(''); }}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${deleteTarget.mode === 'soft' ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-400'}`}
                  data-testid="delete-mode-soft"
                >Archive (soft)</button>
                <button
                  onClick={() => { setDeleteTarget({ ...deleteTarget, mode: 'hard' }); setDeleteError(''); }}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${deleteTarget.mode === 'hard' ? 'bg-white dark:bg-zinc-800 text-red-600 dark:text-red-400 shadow-sm' : 'text-slate-500 dark:text-zinc-400'}`}
                  data-testid="delete-mode-hard"
                >Permanently delete</button>
              </div>

              {deleteTarget.mode === 'soft' ? (
                <p className="text-sm text-slate-600 dark:text-zinc-400">
                  The account will be disabled and hidden from active lists, but the record — including email and phone — is kept for audit. The user cannot log in.
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-zinc-400">
                    This permanently erases the user and <span className="font-medium text-slate-900 dark:text-white">all their data</span> — profile, jobs, matches, screenings, wallet, notifications, WhatsApp history and verification tokens.
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Email <span className="font-mono">{deleteTarget.user.email}</span> and phone <span className="font-mono">{deleteTarget.user.phone || '—'}</span> will be freed and reusable for a new signup. This cannot be undone.
                  </p>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Type the email to confirm</label>
                    <input
                      value={confirmEmail}
                      onChange={e => setConfirmEmail(e.target.value)}
                      placeholder={deleteTarget.user.email}
                      className="w-full bg-white dark:bg-[#0a0a0d] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-red-500"
                      data-testid="delete-confirm-email"
                      autoFocus
                    />
                  </div>
                </div>
              )}

              {deleteError && <p className="text-xs text-red-600 dark:text-red-400" data-testid="delete-error">{deleteError}</p>}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 dark:border-zinc-800">
              <button onClick={closeDelete} className="px-3 py-1.5 text-sm text-slate-600 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-md" data-testid="delete-cancel">Cancel</button>
              <button
                onClick={confirmDelete}
                disabled={deleting || (deleteTarget.mode === 'hard' && confirmEmail.trim().toLowerCase() !== (deleteTarget.user.email || '').toLowerCase())}
                className={`px-3 py-1.5 text-sm rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed ${deleteTarget.mode === 'hard' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting…' : deleteTarget.mode === 'hard' ? 'Permanently delete' : 'Archive user'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
